import { ReactElement, useState } from 'react';

import {
  ChakraFlex,
  ChakraLink,
  ChakraVStack,
  FAIconName,
  FireflyButton,
  ModalV2,
  ReviewPatientCard,
  Text,
} from '@equip.health/ui';

import { unixTimestampToDate } from '~/lib/util/date.util';
import { Path } from '~/routes/constants';

import ConfirmNewProfileCreationModal from './ConfirmNewProfileCreationModal';
import ConfirmPatientProfileMatchModal from './ConfirmPatientProfileMatchModal';

interface ReviewPatientMatchesModalProps {
  id: string;
  isOpen: boolean;
  onCancel: () => void;
  onConfirmPatient: (patientId: string) => void;
  onCreatePatient: () => void;
  patientMatches: PatientMatch[];
}

const ModalContent = {
  subTitle:
    'Please review the existing patient profiles below and select the correct profile if shown.',
  title: 'Review patient matches',
};

const ReviewPatientMatchesModal = ({
  id,
  isOpen,
  onCancel,
  onConfirmPatient,
  onCreatePatient,
  patientMatches,
}: ReviewPatientMatchesModalProps) => {
  const [
    isConfirmPatientProfileMatchModalOpen,
    setIsConfirmPatientProfileMatchModalOpen,
  ] = useState<boolean>(false);

  const [
    isConfirmNewProfileCreationModalOpen,
    setIsConfirmNewProfileCreationModalOpen,
  ] = useState<boolean>(false);

  const [selectedProfile, setSelectedProfile] = useState<PatientMatch>(null);

  const TOP_LEVEL_ID = `${id}__review-patient-matches-modal`;

  const handleCancelConfirmPatientProfileMatchModal = () => {
    setIsConfirmPatientProfileMatchModalOpen(false);
    setSelectedProfile(null);
  };
  const handleUseProfile = (profile: PatientMatch) => {
    setSelectedProfile(profile);
    setIsConfirmPatientProfileMatchModalOpen(true);
  };
  const handleCancelConfirmNewProfileCreationModal = () => {
    setIsConfirmNewProfileCreationModalOpen(false);
  };
  const handleCreateProfile = () => {
    setIsConfirmPatientProfileMatchModalOpen(false);
    setIsConfirmNewProfileCreationModalOpen(true);
  };

  let component: ReactElement;
  if (isConfirmPatientProfileMatchModalOpen) {
    component = (
      <ConfirmPatientProfileMatchModal
        id={`${TOP_LEVEL_ID}__confirm-modal`}
        isOpen={isConfirmPatientProfileMatchModalOpen}
        onCancel={handleCancelConfirmPatientProfileMatchModal}
        onUseProfile={() => {
          onConfirmPatient(selectedProfile.externalId);
          setIsConfirmPatientProfileMatchModalOpen(false);
          onCancel();
        }}
        selectedProfile={selectedProfile}
      />
    );
  } else if (isConfirmNewProfileCreationModalOpen) {
    component = (
      <ConfirmNewProfileCreationModal
        id={`${TOP_LEVEL_ID}__confirm-modal`}
        isOpen={isConfirmNewProfileCreationModalOpen}
        onCancel={handleCancelConfirmNewProfileCreationModal}
        onCancelParent={onCancel}
        onCreateNewProfile={onCreatePatient}
      />
    );
  } else {
    component = (
      <ModalV2
        footerContent={
          <ChakraFlex width="100%">
            <FireflyButton
              id={`${TOP_LEVEL_ID}__confirm`}
              isFullWidth
              leftIcon={FAIconName.userPlusRegular}
              onClick={handleCreateProfile}
              variant="secondary"
            >
              Create new profile
            </FireflyButton>
          </ChakraFlex>
        }
        id={`${TOP_LEVEL_ID}__review-modal`}
        isOpen={isOpen}
        onClose={onCancel}
        size="lg"
        title={ModalContent.title}
      >
        <ChakraVStack gap="18px" width="100%">
          <Text id={`${id}-subtitle`} variant="body">
            {ModalContent.subTitle}
          </Text>
          <ChakraVStack gap="12px" width="100%">
            {patientMatches?.map((p) => {
              return (
                <ReviewPatientCard
                  // TODO: Active inquiry per patient match will be supported once PT-654 is done, see props starting
                  birthDate={p.dateOfBirth ? new Date(p.dateOfBirth) : null}
                  chosenName={p.chosenName}
                  createdDate={unixTimestampToDate(p.createdAt)}
                  firstName={p.firstName}
                  id={`${TOP_LEVEL_ID}__review-patient-card__${p.externalId}`}
                  key={p.externalId}
                  lastName={p.lastName}
                  // @ts-expect-error-next-line TODO fix when the enum in equip-ui is exported so we can properly handle
                  matchedFields={p.matchedFields}
                  onUseProfile={() => handleUseProfile(p)}
                  phoneNumber={p.patientPhoneNumber}
                  viewProfile={
                    <ChakraLink
                      href={`${Path.MAUD_PATIENTS}/${p.externalId}`}
                      id={`${TOP_LEVEL_ID}__view-profile`}
                      isExternal
                    >
                      <FireflyButton
                        id="view"
                        leftIcon={FAIconName.externalLinkRegular}
                        variant="tertiary"
                      >
                        View profile
                      </FireflyButton>
                    </ChakraLink>
                  }
                />
              );
            })}
          </ChakraVStack>
        </ChakraVStack>
      </ModalV2>
    );
  }

  return <>{component}</>;
};

export default ReviewPatientMatchesModal;
